import DefaultImg from "../images/DefaultImg.webp";
import {
  buildMediaBackground,
  getApiOrigin,
  getMediaValue,
  handleMediaError,
} from "./landingMedia";

const PRODUCT_MEDIA_BACKGROUND =
  "linear-gradient(135deg, rgba(73,74,152,.13), rgba(228,78,49,.10))";

const INVALID_MEDIA_LITERALS = new Set([
  "null",
  "undefined",
  "false",
  "true",
  "nan",
  "[object object]",
]);

const INACTIVE_MEDIA_STATUSES = new Set([
  "archived",
  "deleted",
  "historical",
  "inactive",
  "removed",
]);

function isInactiveMediaRecord(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;

  if (
    value.active === false ||
    value.enabled === false ||
    value.archived === true ||
    value.deleted === true ||
    value.historical === true ||
    value.isArchived === true ||
    value.isDeleted === true ||
    value.isHistorical === true ||
    value.isRemoved === true ||
    value.removed === true
  ) {
    return true;
  }

  const status = String(value.status || value.state || "")
    .trim()
    .toLowerCase();
  return INACTIVE_MEDIA_STATUSES.has(status);
}

function isSafeMediaValue(value) {
  const raw = String(value || "").trim();
  if (!raw || INVALID_MEDIA_LITERALS.has(raw.toLowerCase())) return false;
  if (/[\u0000-\u001f<>]/.test(raw)) return false;
  if (/^(javascript|vbscript|file|about):/i.test(raw)) return false;
  if (/^[a-z][a-z0-9+.-]*:/i.test(raw) && !/^(https?|data|blob):/i.test(raw)) {
    return false;
  }
  if (/^data:/i.test(raw)) return /^data:image\//i.test(raw);
  if (/^blob:/i.test(raw)) return /^blob:(https?:|null)/i.test(raw);

  if (/^https?:\/\//i.test(raw)) {
    try {
      const url = new URL(raw);
      return url.protocol === "http:" || url.protocol === "https:";
    } catch {
      return false;
    }
  }

  const normalized = raw.replace(/\\/g, "/");
  const pathOnly = normalized.split(/[?#]/, 1)[0];
  if (!pathOnly || pathOnly.split("/").some((part) => part === "..")) return false;
  return true;
}

function mediaRecordValues(value) {
  if (!value || isInactiveMediaRecord(value)) return [];
  if (typeof value !== "object") return [value];

  return [
    value.publicPath,
    value.path,
    value.publicUrl,
    value.url,
    value.filename,
  ];
}

function appendResolvedCandidate(target, value) {
  const values = mediaRecordValues(value);
  for (const candidate of values) {
    const resolved = resolveProductImage(candidate, "");
    if (!resolved) continue;
    target.push(resolved);
    // Un registro puede exponer url, path y filename a la vez.
    // Sólo se conserva su representación pública más completa.
    break;
  }
}

function canonicalPath(pathname) {
  return String(pathname || "")
    .replace(/\\/g, "/")
    .replace(/\/{2,}/g, "/")
    .replace(/\/$/, "");
}

/**
 * Clave estable para comparar la misma imagen aunque llegue como URL absoluta,
 * /files/... o una ruta histórica relativa.
 */
export function getProductImageKey(input) {
  const resolved = resolveProductImage(input, "");
  if (!resolved) return "";
  if (/^(data:|blob:)/i.test(resolved)) return resolved;

  try {
    const base =
      typeof window !== "undefined" && window.location?.origin
        ? window.location.origin
        : "https://webprofit.invalid";
    const url = new URL(resolved, base);
    const pathname = canonicalPath(url.pathname);

    // Los recursos propios pueden conservar dominios históricos; su ruta
    // dentro de /files, /static o /assets identifica el archivo real.
    if (/^\/(files|static|assets)\//i.test(pathname)) return pathname;

    return `${url.protocol.toLowerCase()}//${url.host.toLowerCase()}${pathname}${url.search}`;
  } catch {
    return canonicalPath(resolved.split("#", 1)[0]);
  }
}

function uniqueResolvedCandidates(candidates) {
  const unique = [];
  const seen = new Set();

  for (const candidate of candidates) {
    const key = getProductImageKey(candidate);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    unique.push(candidate);
  }

  return unique;
}

function collectCurrentProductImages(product) {
  const candidates = [];

  if (Array.isArray(product?.images)) {
    product.images.forEach((value) => appendResolvedCandidate(candidates, value));
  }
  if (Array.isArray(product?.imagesUrls)) {
    product.imagesUrls.forEach((value) => appendResolvedCandidate(candidates, value));
  }

  return uniqueResolvedCandidates(candidates);
}

function collectLegacyProductImages(product) {
  const candidates = [];

  appendResolvedCandidate(candidates, product?.imageUrl);
  if (Array.isArray(product?.imagenes)) {
    product.imagenes.forEach((value) => appendResolvedCandidate(candidates, value));
  }
  appendResolvedCandidate(candidates, product?.image);
  appendResolvedCandidate(candidates, product?.imagen);

  return uniqueResolvedCandidates(candidates);
}

/**
 * Resuelve imágenes históricas y multicliente:
 * - products/<archivo>                         -> /files/products/<archivo>
 * - projects/<tenant>/products/<archivo>      -> /files/projects/<tenant>/products/<archivo>
 * - files/... o /files/...
 * - URL absoluta
 */
export function resolveProductImage(input, fallback = DefaultImg) {
  if (isInactiveMediaRecord(input)) return fallback;

  const raw = getMediaValue(input).trim();
  if (!isSafeMediaValue(raw)) return fallback;
  if (/^(https?:\/\/|data:|blob:)/i.test(raw)) return raw;
  if (raw.startsWith("//")) {
    const protocol = typeof window !== "undefined" ? window.location.protocol : "https:";
    return `${protocol}${raw}`;
  }

  const origin = getApiOrigin();
  const normalized = raw.replace(/\\/g, "/");

  if (normalized.startsWith("/static/") || normalized.startsWith("/assets/")) {
    return normalized;
  }
  if (normalized.startsWith("/files/")) return `${origin}${normalized}`;
  if (normalized.startsWith("files/")) return `${origin}/${normalized}`;
  if (normalized.startsWith("products/")) return `${origin}/files/${normalized}`;
  if (normalized.startsWith("/products/")) return `${origin}/files${normalized}`;
  if (normalized.startsWith("projects/")) return `${origin}/files/${normalized}`;
  if (normalized.startsWith("/projects/")) return `${origin}/files${normalized}`;

  // Los documentos más antiguos pueden tener sólo el nombre del archivo.
  if (!normalized.includes("/")) {
    return `${origin}/files/products/${normalized}`;
  }

  if (normalized.startsWith("/")) return `${origin}${normalized}`;
  return `${origin}/${normalized}`;
}

export function handleProductImageError(event, fallback = DefaultImg) {
  handleMediaError(event, resolveProductImage(fallback, DefaultImg));
}

export function getProductBackgroundImage(input, fallback = PRODUCT_MEDIA_BACKGROUND) {
  return buildMediaBackground(resolveProductImage(input, ""), fallback);
}

export function getProductImageUrls(product = {}, fallback = "") {
  // Cuando existen listas actuales, no se mezclan aliases históricos que
  // podrían conservar referencias ya reemplazadas.
  const current = collectCurrentProductImages(product);
  const urls = current.length ? current : collectLegacyProductImages(product);

  if (!urls.length && fallback) {
    const resolvedFallback = resolveProductImage(fallback, "");
    return resolvedFallback ? [resolvedFallback] : [];
  }
  return urls;
}

export function getProductImage(product, fallback = DefaultImg) {
  return getProductImageUrls(product)[0] || fallback;
}
